import { AdProviderTypes } from '@/types'
import type { AvailablePGEvents, LoggerEvent, StartRumEvent } from '@/types/logger.types'
import { isIframe, timer } from '@/utils/helpers'
import { isElementViewable } from '@/utils/is-element-viewable'

import { type AdLoader } from './ad-loader'
import { AdProvider } from './ad-provider'
import { ConsentManager } from './consent-manager'
import { incrementDailyAdCall } from './daily-ad-call-counter'
import { HeaderBidding } from './header-bidding'
import type { ActiveCmps, HeaderBiddingOptions } from './header-bidding/types'
import { InternalOutOfPageAdBanner } from './providers/internal-out-of-page-ad-banner'
import { InternalPageAdBanner } from './providers/internal-page-ad-banner'
import { PublicOutOfPageAdBanner } from './public-out-of-page-ad-banner'
import { PublicPageAdBanner } from './public-page-ad-banner'
import {
    type AdProviderRequestOptions,
    AdProviderStatus,
    type AdType,
    BannerType,
    type OutOfPageAdType,
    type PageAdOptions,
} from './types'
import { AdConsoleLogger } from './utils/ad-console-logger'
import { createRumTracker } from './utils/create-rum-tracker'
import { generateAdElementId } from './utils/generate-ad-id'

export type ProviderKey = AdType | 'default'

export type FailedAdProvider = {
    readonly failed: true
    readonly type: AdProviderTypes
    readonly errorMessage: string
}

export type LoadingProvider = {
    readonly loading: true
    readonly type: AdProviderTypes
    readonly timeout: number
}

export type ProviderConfig = {
    type: AdProviderTypes
    timeout: number
}

export type AdServiceOptions = {
    consentAccount: string
    getCountryCode?: () => Promise<string | undefined>
    targeting: Record<string, string | string[]>
    logEvent: (event: LoggerEvent) => void
    registerPageHideFallbackEvent: (event: Omit<LoggerEvent, 'dimension1'>) => () => void
    startRumEvent: StartRumEvent
    headerBiddingConfig?: HeaderBiddingOptions
    auid: string
    providersSettings: Partial<Record<ProviderKey, ProviderConfig[]>> | ProviderConfig[]
}

const SERVICE_INITIALIZATION_TIMEOUT = 10000
const GET_USER_SIGNALS_TIMEOUT = 100

export class AdService {
    private readonly options: AdServiceOptions

    private readonly logEvent: (event: LoggerEvent) => void

    private readonly startRumEvent: StartRumEvent

    private providers: Partial<Record<ProviderKey, (AdProvider | FailedAdProvider | LoadingProvider)[]>> = {}

    private currentTargeting: Record<string, string | string[]> = {}

    private headerBidding: HeaderBidding | undefined = undefined

    private depsReady = false
    private serviceReady = false

    serviceReadyPromise: Promise<AdProviderStatus>
    private serviceReadyResolve: (status: AdProviderStatus) => void

    pageBanners: Set<PublicPageAdBanner> = new Set()

    private readonly refreshTimers: Map<PublicPageAdBanner, number> = new Map()

    outOfPageBanners: Set<PublicOutOfPageAdBanner> = new Set()

    consentManager: ConsentManager

    adConsoleLogger = new AdConsoleLogger('service')

    constructor(options: AdServiceOptions) {
        this.adConsoleLogger.debug('create service', { options })
        this.options = options
        this.consentManager = new ConsentManager({
            handleConsentlessEnv: async () => {
                const countryCode = await this.options.getCountryCode?.()
                return countryCode === 'US'
            },
            consentAccount: this.options.consentAccount,
            logEvent: this.options.logEvent,
        })
        this.logEvent = options.logEvent
        this.startRumEvent = options.startRumEvent
        const providersSettings = Array.isArray(options.providersSettings)
            ? { default: options.providersSettings }
            : options.providersSettings

        for (const [type, providers] of Object.entries(providersSettings)) {
            this.providers[type as ProviderKey] = providers.map(({ type: providerType, timeout }) => ({
                loading: true,
                type: providerType,
                timeout,
            }))
        }
        this.currentTargeting = options.targeting || {}

        const { promise, resolve } = Promise.withResolvers<AdProviderStatus>()
        this.serviceReadyPromise = promise
        this.serviceReadyResolve = resolve

        timer(SERVICE_INITIALIZATION_TIMEOUT).then(() => this.markServiceReady('timeout'))

        this.initDeps()
    }

    setProvider(index: number, provider: AdProvider | FailedAdProvider, type: ProviderKey = 'default') {
        const arr = this.providers[type]
        if (arr && index < arr.length) {
            arr[index] = provider
        }

        if ('failed' in provider) {
            this.adConsoleLogger.debug(`provider failed`, { type, index, error: provider.errorMessage })
        } else {
            this.adConsoleLogger.debug(`provider ready`, { type, index, provider: provider.type })
            this.setProviderTargeting(provider, this.currentTargeting)
        }
        this.checkServiceReady()
    }

    private async initDeps() {
        const tracker = createRumTracker('ad-service-deps-init', this.startRumEvent)
        if (isIframe()) {
            const activeCmps = await this.consentManager.install()
            await this.initHeaderBidding(activeCmps)
        } else {
            await Promise.all([this.consentManager.install(), this.initHeaderBidding({ gpp: true, tcf: true })])
        }
        tracker.send({ status: 'success' })
        this.depsReady = true
        this.checkServiceReady()
    }

    private async initHeaderBidding(activeCmps: ActiveCmps): Promise<void> {
        if (this.options.headerBiddingConfig) {
            this.headerBidding = new HeaderBidding(this.options.headerBiddingConfig, activeCmps)
            await this.headerBidding.ready
            this.headerBidding.updateTargeting(this.currentTargeting)
            await Promise.race([this.headerBidding.getUserSignals(), timer(GET_USER_SIGNALS_TIMEOUT)])
        }
    }

    private checkServiceReady() {
        if (this.serviceReady) {
            return
        }

        const allTypesReady = Object.values(this.providers).every(
            (slots) =>
                slots.some((p): p is AdProvider => !('loading' in p) && !('failed' in p) && !!p.blocksServiceReady) ||
                slots.every((p) => !('loading' in p)),
        )

        if (allTypesReady && this.depsReady) {
            this.markServiceReady()
        }
    }

    private markServiceReady(reason?: string) {
        if (!this.serviceReady) {
            if (reason === 'timeout') {
                this.logEvent({
                    event: 'custom_event',
                    eventName: 'ad_service_ready_timeout',
                })
            } else {
                this.logEvent({
                    event: 'custom_event',
                    eventName: 'ad_service_ready',
                })
            }

            this.serviceReady = true
            this.adConsoleLogger.debug(reason ? `service ready (${reason})` : 'service ready')
            this.serviceReadyResolve(AdProviderStatus.online)
        }
    }

    private getProvidersByType(type: ProviderKey): Array<AdProvider | FailedAdProvider | LoadingProvider> {
        return this.providers[type] || this.providers.default || []
    }

    private getActiveProvidersByType(type: ProviderKey): AdProvider[] {
        return this.getProvidersByType(type).filter((p): p is AdProvider => !('loading' in p) && !('failed' in p))
    }

    private async requestAd<T extends InternalPageAdBanner | InternalOutOfPageAdBanner>(
        providerMethod: (provider: AdProvider, requestOptions: AdProviderRequestOptions) => T,
        publicBanner: {
            setIntermediateInternalBanner: (internalBanner: T) => void
            setInternalBanner: (internalBanner: T) => void
            triggerEmpty: () => void
        },
        { type, adSource }: { type: AdType; adSource?: string },
    ): Promise<boolean> {
        const requestTracker = createRumTracker('request-ad', this.startRumEvent)
        const providerSlots = this.getProvidersByType(type)
        this.adConsoleLogger.debug(`waterfall start`, { placement: type, providers: providerSlots.length })

        for (let i = 0; i < providerSlots.length; i++) {
            const stepTracker = createRumTracker('request-ad-step', this.startRumEvent)

            const orderAdValue = i + 1
            const currentProvider = providerSlots[i]
            const cancelPageHiddenTracking = this.options.registerPageHideFallbackEvent({
                event: 'custom_event',
                eventName: 'ad_request_attempt_failed',
                adPlacement: type,
                adProvider: currentProvider?.type,
                waterfallIndex: orderAdValue,
                ...(adSource && { dimension2: adSource }),
                dimension3: 'page_hide_fallback',
            })

            this.adConsoleLogger.debug(`waterfall attempt [${orderAdValue}/${providerSlots.length}]`, {
                placement: type,
                provider: currentProvider?.type || 'unknown',
            })

            this.logEvent({
                event: 'custom_event',
                eventName: 'ad_waterfall_attempt',
                adPlacement: type,
                adProvider: currentProvider?.type,
                waterfallIndex: orderAdValue,
                ...(adSource && { dimension2: adSource }),
                dimension3: `${providerSlots.length}`,
            })

            if (!currentProvider || 'failed' in currentProvider || 'loading' in currentProvider) {
                let logErrorMessage = 'Provider is not initialized: unexpected error'

                if (currentProvider && 'failed' in currentProvider) {
                    logErrorMessage = `Failed to load provider: ${currentProvider.errorMessage}`
                }

                if (currentProvider && 'loading' in currentProvider) {
                    logErrorMessage = 'Provider is not initialized'
                }

                stepTracker.send({
                    status: 'error',
                    dimension2: logErrorMessage,
                    dimension4: type,
                    dimension3: `${orderAdValue}`,
                })
                this.logEvent({
                    event: 'custom_event',
                    eventName: 'ad_request_attempt_failed',
                    adPlacement: type,
                    waterfallIndex: orderAdValue,
                    errorMessage: logErrorMessage,
                    adProvider: currentProvider?.type,
                    dimension3: logErrorMessage,
                    ...(adSource && { dimension2: adSource }),
                })
                cancelPageHiddenTracking()

                continue
            }

            let internalBanner: T | undefined

            const log = (
                eventName: AvailablePGEvents,
                rest?: Partial<Omit<LoggerEvent, 'dimension1' | 'dimension2'>>,
            ) => {
                this.logEvent({
                    event: 'custom_event',
                    eventName,
                    adPlacement: type,
                    adUnit: internalBanner?.adUnit,
                    adHbCpmUsd: internalBanner?.headerBiddingAuctionResult?.renderedBid?.cpm ?? undefined,
                    adHbBidder: internalBanner?.headerBiddingAuctionResult?.renderedBid?.bidder,
                    adHbRequestId: internalBanner?.headerBiddingAuctionResult?.renderedBid?.requestId || undefined,
                    adProvider: currentProvider.type,
                    instreamDuration:
                        internalBanner?.type === BannerType.OUT_OF_PAGE ? internalBanner?.instreamDuration : undefined,
                    waterfallIndex: orderAdValue,
                    dimension1: internalBanner?.headerBiddingAuctionResult?.winningBid?.mediaType,
                    dimension2: adSource,
                    ...rest,
                })
            }

            let requestAcknowledged = false

            try {
                const adUnit = currentProvider.getAdUnitByType(type) || type

                currentProvider.updateTargeting({
                    daily_call: `${incrementDailyAdCall(adUnit)}`,
                })

                internalBanner = providerMethod(currentProvider, { headerBidding: this.headerBidding })

                publicBanner.setIntermediateInternalBanner(internalBanner)
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : `Cannot show the ad ${type}`
                stepTracker.send({
                    status: 'error',
                    dimension1: currentProvider.type,
                    dimension2: errorMessage,
                    dimension4: type,
                    dimension3: `${orderAdValue}`,
                })
                log('ad_request_unexpected_error', {
                    errorMessage,
                    dimension3: errorMessage,
                })
                cancelPageHiddenTracking()
                continue
            }

            internalBanner.addEventListener('request', () => {
                requestAcknowledged = true
                log('ad_request')
            })

            internalBanner.addEventListener('unexpected_behavior', (e) => {
                log('ad_unexpected_behavior', { dimension3: e.detail?.reason })
            })

            internalBanner.addEventListener('viewable', () => {
                if (i === 0) {
                    log('ad_show_high_value')
                }
                stepTracker.send({
                    status: 'success',
                    dimension4: type,
                    dimension1: currentProvider.type,
                    dimension2: internalBanner.adUnit,
                    dimension3: `${orderAdValue}`,
                })
                requestTracker.send({
                    status: 'success',
                    dimension4: type,
                    dimension3: `${orderAdValue}`,
                })
                log('ad_show')
            })

            if (internalBanner.type === BannerType.IN_PAGE) {
                internalBanner.addEventListener('showing', () => {
                    log('ad_rendered')
                })
            }

            const succeeded = await new Promise<boolean>((resolve) => {
                internalBanner.addEventListener(
                    internalBanner.type === BannerType.OUT_OF_PAGE ? 'ready' : 'showing',
                    () => {
                        resolve(true)
                    },
                    { once: true },
                )
                internalBanner.addEventListener(
                    'validation_error',
                    (e) => {
                        requestAcknowledged = true
                        log('ad_request_attempt_failed', { dimension3: e.detail?.reason })
                        resolve(false)
                    },
                    { once: true },
                )
                internalBanner.addEventListener(
                    'empty',
                    (e) => {
                        if (!requestAcknowledged) {
                            log('ad_unexpected_behavior', {
                                dimension3: `Empty without request with reason: ${e.detail?.reason}`,
                            })
                        }
                        log('ad_empty', { dimension3: e.detail?.reason })
                        stepTracker.send({
                            status: 'empty',
                            dimension4: type,
                            dimension1: currentProvider.type,
                            dimension2: internalBanner.adUnit,
                            dimension3: `${orderAdValue}`,
                        })
                        resolve(false)
                    },
                    { once: true },
                )
            })

            cancelPageHiddenTracking()

            if (succeeded) {
                publicBanner.setInternalBanner(internalBanner)
                return true
            }
        }
        this.adConsoleLogger.debug(`waterfall exhausted, no fill`, { placement: type, attempts: providerSlots.length })

        requestTracker.send({
            status: 'empty',
            dimension4: type,
        })

        this.logEvent({
            event: 'custom_event',
            eventName: 'ad_request_failed',
            waterfallIndex: providerSlots.length,
            adProvider: 'unknown',
            adPlacement: type,
            ...(adSource && { dimension2: adSource }),
        })

        publicBanner.triggerEmpty()
        return false
    }

    requestOutOfPageAd(
        type: OutOfPageAdType,
        adSource?: string,
        options?: { loader?: AdLoader; showAdFreePromo?: boolean },
    ): PublicOutOfPageAdBanner {
        const { loader, showAdFreePromo } = options ?? {}
        const publicBanner = new PublicOutOfPageAdBanner({
            placement: type,
            onDestroy: () => {
                this.outOfPageBanners.delete(publicBanner)
            },
            loader,
            timeout: this.getAdMaxTimeout(type),
        })

        this.outOfPageBanners.add(publicBanner)
        publicBanner.addEventListener(
            'show_not_called_after_ready',
            () => {
                this.logEvent({
                    event: 'custom_event',
                    eventName: 'ad_show_not_called_after_ready',
                    adPlacement: type,
                    adUnit: publicBanner.adUnit,
                    adProvider: publicBanner.provider,
                    dimension2: adSource,
                })
            },
            { once: true },
        )
        this.adConsoleLogger.debug(`ad requested`, { placement: type, adSource })

        this.yieldToListeners().then(async () => {
            await this.serviceReadyPromise
            const succeeded = await this.requestAd<InternalOutOfPageAdBanner>(
                (provider, requestOptions) => provider.prepareOutOfPageAd(type, { ...requestOptions, showAdFreePromo }),
                publicBanner,
                { type, adSource },
            )
            if (succeeded) {
                publicBanner.triggerReady()
            }
        })

        return publicBanner
    }

    requestPageAd(options: PageAdOptions): PublicPageAdBanner {
        const publicBanner: PublicPageAdBanner = new PublicPageAdBanner({
            placement: options.type,
            onDestroy: () => {
                this.pageBanners.delete(publicBanner)
                window.clearTimeout(this.refreshTimers.get(publicBanner))
                this.refreshTimers.delete(publicBanner)
            },
            onRefresh: () => {
                this.loadPageAd(publicBanner, options)
            },
        })

        this.pageBanners.add(publicBanner)
        this.adConsoleLogger.debug(`ad requested`, { placement: options.type })

        this.yieldToListeners().then(async () => {
            await this.loadPageAd(publicBanner, options)

            if (options.refresh) {
                this.scheduleRefresh(publicBanner, options.refresh)
            }
        })

        return publicBanner
    }

    private async loadPageAd(publicBanner: PublicPageAdBanner, options: PageAdOptions): Promise<void> {
        await this.serviceReadyPromise

        // Banner may have been destroyed while awaiting serviceReadyPromise (e.g. ad-free mode activated)
        if (!this.pageBanners.has(publicBanner)) {
            return
        }

        const container = this.createAdContainer(options.el)

        if (!container) {
            publicBanner.triggerEmpty('no_container')
            return
        }

        const succeeded = await this.requestAd<InternalPageAdBanner>(
            (provider, requestOptions) => provider.requestPageAd({ ...options, el: container.id }, requestOptions),
            {
                setInternalBanner: (internalBanner) => publicBanner.setInternalBanner(internalBanner, container),
                setIntermediateInternalBanner: (internalBanner) =>
                    publicBanner.setIntermediateInternalBanner(internalBanner),
                triggerEmpty: () => publicBanner.triggerEmpty(),
            },
            { type: options.type },
        )

        if (succeeded) {
            publicBanner.triggerRendered()
        } else {
            container.remove()
        }
    }

    /** Defer past the microtask queue so callers can attach listeners before any provider event fires. */
    private yieldToListeners(): Promise<void> {
        return timer(10)
    }

    private createAdContainer(slotId: string): HTMLElement | null {
        const slot = document.getElementById(slotId)
        if (!slot) {
            return null
        }
        const container = document.createElement('div')
        container.id = generateAdElementId()
        slot.appendChild(container)
        return container
    }

    private scheduleRefresh(publicBanner: PublicPageAdBanner, refreshInterval: number): void {
        const timerId = window.setTimeout(() => {
            const isViewable = publicBanner.containerElId && isElementViewable(publicBanner.containerElId)
            if (isViewable) {
                publicBanner.refresh()
            }
            this.scheduleRefresh(publicBanner, refreshInterval)
        }, refreshInterval * 1000)
        this.refreshTimers.set(publicBanner, timerId)
    }

    setOutOfPageAdPlaybackState(play: boolean): void {
        for (const publicBanner of this.outOfPageBanners) {
            publicBanner.setPlaybackState(play)
        }
    }

    shutdown() {
        for (const publicBanner of this.pageBanners) {
            publicBanner.destroy()
        }
        for (const publicBanner of this.outOfPageBanners) {
            publicBanner.gracefullyDestroy('ad_free_mode_enabled')
        }
    }

    getTargeting(): Record<string, string | string[]> {
        return structuredClone(this.currentTargeting)
    }

    updateTargeting(targeting: Record<string, string | string[]>) {
        for (const key in targeting) {
            if (targeting[key]) {
                this.currentTargeting[key] = targeting[key]
            } else {
                delete this.currentTargeting[key]
            }
        }

        this.headerBidding?.updateTargeting(this.currentTargeting)

        for (const key in this.providers) {
            this.getActiveProvidersByType(key as ProviderKey).forEach((provider) => {
                this.setProviderTargeting(provider, this.currentTargeting)
            })
        }
    }

    private setProviderTargeting(provider: AdProvider, targeting: Record<string, string | string[]>) {
        try {
            provider.updateTargeting(targeting)
        } catch {
            // provider threw an error, nothing we can do
        }
    }

    getAdMaxTimeout(type?: AdType): number {
        return this.getProvidersByType(type || 'default')
            .filter((p): p is AdProvider | LoadingProvider => !('failed' in p))
            .reduce((accumulator, provider) => accumulator + provider.timeout, 0)
    }
}
